import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase.js'

export default function useAuth() {
  const [session, setSession] = useState(null)
  const [member, setMember] = useState(null)
  const [isSessionLoading, setIsSessionLoading] = useState(Boolean(supabase))
  const [isMembershipLoading, setIsMembershipLoading] = useState(false)
  const [authError, setAuthError] = useState('')
  const [membershipError, setMembershipError] = useState('')

  useEffect(() => {
    if (!supabase) return undefined
    let active = true

    const resolveSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession()
        if (!active) return
        if (error) setAuthError(error.message)
        setSession(data.session)
      } catch (error) {
        if (active) setAuthError(error instanceof Error ? error.message : 'Unable to restore the session.')
      } finally {
        if (active) setIsSessionLoading(false)
      }
    }

    resolveSession()
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!active) return
      setSession(nextSession)
      setAuthError('')
      if (!nextSession) {
        setMember(null)
        setMembershipError('')
        setIsMembershipLoading(false)
      }
    })

    return () => {
      active = false
      listener.subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!supabase || !session?.user?.id) return undefined
    let active = true
    setMember(null)
    setMembershipError('')
    setIsMembershipLoading(true)

    const resolveMembership = async () => {
      try {
        const { data, error } = await supabase
          .from('app_members')
          .select('user_id, role, display_name, active')
          .eq('user_id', session.user.id)
          .maybeSingle()
        if (!active) return
        if (error) {
          setMembershipError(error.message)
          return
        }
        setMember(data)
      } catch (error) {
        if (active) setMembershipError(error instanceof Error ? error.message : 'Unable to verify Botanical membership.')
      } finally {
        if (active) setIsMembershipLoading(false)
      }
    }

    resolveMembership()
    return () => { active = false }
  }, [session?.user?.id])

  return {
    session,
    member,
    isSessionLoading,
    isMembershipLoading,
    authError,
    membershipError,
    signOut: () => supabase?.auth.signOut(),
  }
}
